import {
  Component,
  ElementRef,
  Injector,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core'
import { AbstractComponent } from 'lib-trend-core'

/** Pseudo-waveform resolution — enough bars to read as audio, cheap to render. */
const BAR_COUNT = 28

/** Speed toggle cycle (RF-07) — persisted per browser session. */
const PLAYBACK_RATES: number[] = [1, 1.5, 2]

/** sessionStorage key persisting the chosen playback rate across players. */
const RATE_STORAGE_KEY = 'trendmulti.chat.audioPlaybackRate'

/** Arrow-key scrub step on the waveform slider. */
const SEEK_STEP_SECONDS = 5

/**
 * Chromium bug (crbug.com/642012): MediaRecorder WebM blobs carry no duration
 * header, so `audio.duration` reports Infinity until a far seek forces the
 * demuxer to index the file. Seeking to this sentinel triggers that indexing.
 */
const DURATION_PROBE_SECONDS = 1e7

/**
 * Internal Chat audio player (SPEC-2026-07-internal-chat, T-34).
 *
 * Renders any AUDIO chat message / `audio/*` attachment with:
 * - play/pause (only one player active at a time — starting one pauses the
 *   previous, tracked via a static ref);
 * - static pseudo-waveform: bar heights are DETERMINISTIC from the message id
 *   (FNV-1a seed + xorshift), so the same message always draws the same shape
 *   in every session — no decoding cost;
 * - click-to-scrub on the waveform (also keyboard: ←/→ seek ±5 s, slider role);
 * - current/total time with the Chromium Infinity-duration probe for
 *   MediaRecorder WebM blobs (see DURATION_PROBE_SECONDS);
 * - 1×/1.5×/2× speed toggle persisted in sessionStorage (shared by all
 *   players, applied on play);
 * - download link (cross-origin `download` attribute is ignored by browsers —
 *   Firebase URLs open in a new tab, same caveat as the T-33 file card).
 *
 * BROWSER CAVEAT: audio recorded on Chromium (audio/webm;codecs=opus) may not
 * play on older Safari (WebM/Opus playback landed in Safari ~17.4). Safari
 * records audio/mp4 (AAC), which every engine plays.
 */
@Component({
  selector: 'chat-audio-player',
  templateUrl: './chat-audio-player.component.html',
  styleUrls: ['./chat-audio-player.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatAudioPlayerComponent
  extends AbstractComponent
  implements OnChanges
{
  /** The player currently audible — starting another pauses this one. */
  private static activePlayer: ChatAudioPlayerComponent | null = null

  @ViewChild('audioRef') audioRef: ElementRef<HTMLAudioElement>
  @ViewChild('waveform') waveform: ElementRef<HTMLDivElement>

  /** Audio file URL (chat message `fileUrl`). */
  @Input() src: string

  /** Waveform seed — use a STABLE id (clientMessageId survives the optimistic
   *  → persisted swap; `_id` changes on reconcile and would redraw the shape). */
  @Input() messageId: string

  /** Original file name — used by the download link. */
  @Input() fileName?: string

  /** Bar heights in % (25–100), deterministic from `messageId`. */
  bars: number[] = []

  playing = false
  currentTime = 0
  duration = 0
  rate = 1

  /**
   * The source could not be loaded or decoded. Until now these failures were
   * swallowed: `play()` rejected, `playing` went back to false, and the button
   * simply did nothing — indistinguishable from a slow network. Codec mismatches
   * are a real case here (see the Safari/WebM caveat above), so the failure has
   * to be visible and recoverable.
   */
  failed = false

  /** Scrub requested before the metadata arrived — applied on durationchange. */
  private pendingSeekFraction: number | null = null

  /** Infinity-duration probe in flight (see DURATION_PROBE_SECONDS). */
  private probingDuration = false

  constructor(injector: Injector) {
    super(injector)
    this.rate = this.readStoredRate()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['messageId'] || changes['src']) {
      this.bars = this.buildBars(this.messageId || this.src || '')
    }
    if (changes['src'] && !changes['src'].firstChange) {
      // New source under the same component instance — reset playback state.
      this.playing = false
      this.currentTime = 0
      this.duration = 0
      this.pendingSeekFraction = null
      this.probingDuration = false
      this.failed = false
    }
  }

  override ngOnDestroy(): void {
    try {
      this.audioRef?.nativeElement?.pause()
    } catch {
      /* already detached */
    }
    if (ChatAudioPlayerComponent.activePlayer === this) {
      ChatAudioPlayerComponent.activePlayer = null
    }
    super.ngOnDestroy()
  }

  // ---------------------------------------------------------------------------
  // View-model
  // ---------------------------------------------------------------------------

  get playedBars(): number {
    if (this.duration <= 0) return 0
    return Math.round((this.currentTime / this.duration) * BAR_COUNT)
  }

  /**
   * Metadata still in flight — `preload="metadata"` has not resolved a duration.
   * NOT named `loading`: AbstractComponent already owns that property for its own
   * HTTP spinner, and shadowing it with a getter is a compile error (TS2611).
   */
  get awaitingMetadata(): boolean {
    return !this.failed && this.duration <= 0
  }

  /**
   * Untouched audio shows only its length, which is the one thing you want to
   * know before pressing play; the running count appears once it is running.
   * It used to always read `00:00 / --:--`, which spends the line on two values
   * that are both meaningless while idle.
   */
  get timeLabel(): string {
    if (this.awaitingMetadata) return '--:--'
    const total = this.formatTime(this.duration)
    if (!this.playing && this.currentTime <= 0) return total
    return `${this.formatTime(this.currentTime)} / ${total}`
  }

  /** "1×" · "1.5×" · "2×" — technical notation, intentionally untranslated. */
  get rateLabel(): string {
    return `${this.rate}×`
  }

  get ariaValueNow(): number {
    return Math.round(this.currentTime)
  }

  get ariaValueMax(): number {
    return Math.max(0, Math.round(this.duration))
  }

  // ---------------------------------------------------------------------------
  // Controls
  // ---------------------------------------------------------------------------

  toggle(): void {
    const audio = this.audioRef?.nativeElement
    if (!audio || !this.src || this.failed) return

    if (this.playing) {
      audio.pause()
      return
    }

    audio.playbackRate = this.rate
    audio.play().catch(() => {
      this.playing = false
      this.failed = true
    })
  }

  /** `<audio>` could not fetch or decode the source. */
  onError(): void {
    this.playing = false
    this.probingDuration = false
    this.failed = true
  }

  /**
   * Re-arms the element after a failure. `load()` alone would re-read the same
   * cached failure, so the src is cycled to force a fresh request — the usual
   * case is a transient network error on the signed file URL.
   */
  retry(): void {
    const audio = this.audioRef?.nativeElement
    if (!audio) return

    this.failed = false
    this.playing = false
    this.currentTime = 0
    this.duration = 0
    this.pendingSeekFraction = null
    this.probingDuration = false

    audio.removeAttribute('src')
    audio.load()
    audio.src = this.src
    audio.load()
  }

  cycleRate(): void {
    const index = PLAYBACK_RATES.indexOf(this.rate)
    this.rate = PLAYBACK_RATES[(index + 1) % PLAYBACK_RATES.length]
    try {
      sessionStorage.setItem(RATE_STORAGE_KEY, String(this.rate))
    } catch {
      /* storage unavailable — rate just not persisted */
    }
    const audio = this.audioRef?.nativeElement
    if (audio) audio.playbackRate = this.rate
  }

  onWaveClick(event: MouseEvent): void {
    const element = this.waveform?.nativeElement
    if (!element) return
    const rect = element.getBoundingClientRect()
    if (rect.width <= 0) return
    const fraction = Math.min(
      1,
      Math.max(0, (event.clientX - rect.left) / rect.width),
    )
    this.seekToFraction(fraction)
  }

  onWaveKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    if (this.duration <= 0) return
    const delta =
      event.key === 'ArrowRight' ? SEEK_STEP_SECONDS : -SEEK_STEP_SECONDS
    const next = Math.min(this.duration, Math.max(0, this.currentTime + delta))
    this.seekToFraction(next / this.duration)
  }

  // ---------------------------------------------------------------------------
  // <audio> events
  // ---------------------------------------------------------------------------

  onLoadedMetadata(): void {
    const audio = this.audioRef?.nativeElement
    if (!audio) return

    if (isFinite(audio.duration) && audio.duration > 0) {
      this.duration = audio.duration
      this.applyPendingSeek()
      return
    }

    // Chromium Infinity-duration WebM — force the demuxer to index the file.
    this.probingDuration = true
    try {
      audio.currentTime = DURATION_PROBE_SECONDS
    } catch {
      this.probingDuration = false
    }
  }

  onDurationChange(): void {
    const audio = this.audioRef?.nativeElement
    if (!audio || !isFinite(audio.duration) || audio.duration <= 0) return

    this.duration = audio.duration
    if (this.probingDuration) {
      this.probingDuration = false
      audio.currentTime =
        this.pendingSeekFraction != null
          ? this.pendingSeekFraction * this.duration
          : 0
      this.pendingSeekFraction = null
      this.currentTime = audio.currentTime
      // The probe seek may have interrupted an in-flight play() — resume.
      if (this.playing)
        audio.play().catch(() => {
          this.playing = false
        })
      return
    }
    this.applyPendingSeek()
  }

  onTimeUpdate(): void {
    if (this.probingDuration) return // sentinel position — not real progress
    const audio = this.audioRef?.nativeElement
    if (!audio) return
    this.currentTime = audio.currentTime
  }

  onEnded(): void {
    if (this.probingDuration) return
    this.playing = false
    this.currentTime = 0
    const audio = this.audioRef?.nativeElement
    if (audio) audio.currentTime = 0 // parked at the start for replay
  }

  onPlay(): void {
    const previous = ChatAudioPlayerComponent.activePlayer
    if (previous && previous !== this) {
      try {
        previous.audioRef?.nativeElement?.pause()
      } catch {
        /* previous player already gone */
      }
    }
    ChatAudioPlayerComponent.activePlayer = this
    this.playing = true
    const audio = this.audioRef?.nativeElement
    if (audio) audio.playbackRate = this.rate
  }

  onPause(): void {
    this.playing = false
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private seekToFraction(fraction: number): void {
    const audio = this.audioRef?.nativeElement
    if (!audio) return

    if (this.duration > 0 && !this.probingDuration) {
      audio.currentTime = fraction * this.duration
      this.currentTime = audio.currentTime
      return
    }
    // Metadata not there yet (preload deferred / probe running) — remember the
    // target and let durationchange land it.
    this.pendingSeekFraction = fraction
  }

  private applyPendingSeek(): void {
    if (this.pendingSeekFraction == null || this.duration <= 0) return
    const fraction = this.pendingSeekFraction
    this.pendingSeekFraction = null
    const audio = this.audioRef?.nativeElement
    if (!audio) return
    audio.currentTime = fraction * this.duration
    this.currentTime = audio.currentTime
  }

  /**
   * Deterministic pseudo-waveform: FNV-1a over the seed, then xorshift32 per
   * bar — heights between 25% and 100%. Same seed ⇒ same shape, every session.
   */
  private buildBars(seed: string): number[] {
    let hash = 2166136261
    for (let i = 0; i < seed.length; i++) {
      hash ^= seed.charCodeAt(i)
      hash = Math.imul(hash, 16777619)
    }
    const bars: number[] = []
    for (let i = 0; i < BAR_COUNT; i++) {
      hash ^= hash << 13
      hash ^= hash >>> 17
      hash ^= hash << 5
      hash |= 0
      bars.push(25 + (Math.abs(hash) % 76)) // 25..100
    }
    return bars
  }

  private formatTime(seconds: number): string {
    const safe = Math.max(0, Math.floor(seconds || 0))
    const minutes = Math.floor(safe / 60)
      .toString()
      .padStart(2, '0')
    const rest = (safe % 60).toString().padStart(2, '0')
    return `${minutes}:${rest}`
  }

  private readStoredRate(): number {
    try {
      const stored = Number(sessionStorage.getItem(RATE_STORAGE_KEY))
      return PLAYBACK_RATES.includes(stored) ? stored : PLAYBACK_RATES[0]
    } catch {
      return PLAYBACK_RATES[0]
    }
  }
}
